import { getSrc } from 'gatsby-plugin-image';
import { APP_ROOT } from 'utils/constants/urls';
import { AllProductInfoType, FaqSchema } from 'utils/types';
import { PDPPageReview } from 'utils/types';

import {
  discountedPrice,
  Promo,
  promoAppliesToProduct,
} from '../../utils/helpers/activePromoHelpers';
import {
  Availability,
  FaqJSONLDSchema,
  ItemListJSONLDSchema,
  ProductJSONLDSchema,
  SchemaType,
} from './types';

const config = require('../../utils/constants/siteConfig');

const schemaContext = 'https://schema.org';

const productURL = (slug: string): string => `${APP_ROOT}/products/${slug}`;

const listItem = (slug: string, index: number) => ({
  '@type': SchemaType.LIST_ITEM,
  position: index + 1,
  url: productURL(slug),
});

export const structuredProductList = (
  slugs: string[],
): ItemListJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.ITEM_LIST,
  itemListElement: slugs.map((slug, i) => listItem(slug, i)),
});

export const structuredProductData = (
  productData: AllProductInfoType,
  promo?: Promo,
  regionalRestrictionApplies?: boolean,
): ProductJSONLDSchema => {
  const {
    productId,
    name,
    description,
    slug,
    sku,
    upc,
    price: basePrice,
    reviewData,
    outOfStock,
    heroSection,
    promotionable,
  } = productData;

  const url = productURL(slug);
  const image = getSrc(heroSection.heroSliderImages[0].gatsbyImageData);

  const gtin12 = upc || null;

  // Google crawlers expect "price" to match Google Merchant Feed "sale_price"
  // whenever one is set, otherwise it should be the base price
  const promoApplicable =
    promo && promoAppliesToProduct(productId, promo, promotionable);
  const price = discountedPrice(basePrice, promoApplicable ? promo : undefined);
  const formattedPrice = Number(price).toFixed(2);
  const availability =
    outOfStock || regionalRestrictionApplies
      ? Availability.OUT_OF_STOCK
      : Availability.IN_STOCK;
  const offerSku = productId.toString();

  // This field should be set to when the promotion expires, otherwise
  // we dynamically set it to an year from now to ensure it gets populated
  const priceValidUntilDate = new Date();
  priceValidUntilDate.setHours(0, 0, 0, 0);
  priceValidUntilDate.setFullYear(priceValidUntilDate.getFullYear() + 1);

  const priceValidUntil = promoApplicable
    ? new Date(promo.expires_at).toISOString()
    : priceValidUntilDate.toISOString();

  const brand = {
    '@type': SchemaType.BRAND,
    name: 'Everlywell',
  };

  // Review Snippets requires non-zero positive values, so it's best not to include it.
  // This usually happens when we do not have any review data and is common
  // with newly launched and unpopular products)
  let aggregateRating;
  if (reviewData?.average_rating > 0 && reviewData?.review_count > 0) {
    aggregateRating = {
      '@type': SchemaType.AGGREGATE_RATING,
      ratingValue: reviewData?.average_rating,
      reviewCount: reviewData?.review_count,
    };
  }

  const review = reviewData?.contents?.map((review: PDPPageReview) => ({
    '@type': SchemaType.REVIEW,
    name: review.headline,
    reviewBody: review.comments,
    datePublished: new Date(review.created_date).toISOString(),
    author: {
      '@type': SchemaType.PERSON,
      name: review.author,
    },
    reviewRating: {
      '@type': SchemaType.RATING,
      ratingValue: review.rating,
    },
    locationCreated: {
      '@type': SchemaType.LOCATION_CREATED,
      name: review.location,
    },
  }));

  const offers = {
    '@type': SchemaType.OFFER,
    sku: offerSku,
    availability,
    price: formattedPrice,
    priceValidUntil,
    priceCurrency: 'USD',
    url,
  };

  // This schema must match the data provided to the Google Merchant Feed
  // These values are pulled from Contentful and Spree.
  return {
    '@context': schemaContext,
    '@type': SchemaType.PRODUCT,
    name,
    brand,
    description,
    sku,
    gtin12,
    url,
    image,
    aggregateRating,
    review,
    offers,
  };
};

// These values are defined at siteConfig.
export const structuredOrgData = {
  '@context': schemaContext,
  '@type': SchemaType.ORGANIZATION,
  name: config.author,
  legalName: config.legalName,
  url: config.authorUrl,
  logo: config.siteLogo,
  foundingDate: config.foundingDate,
  founders: {
    '@type': SchemaType.PERSON,
    name: config.founder,
  },
  address: {
    '@type': SchemaType.POSTAL_ADDRESS,
    streetAddress: config.streetAddress,
    addressLocality: config.addressLocality,
    addressRegion: config.addressRegion,
    postalCode: config.postalCode,
    addressCountry: config.addressCountry,
  },
  contactPoint: {
    '@type': SchemaType.CONTACT_POINT,
    contactType: config.contactType,
    telephone: config.telephone,
    email: config.email,
  },
  sameAs: [config.facebook, config.twitter, config.pinterest, config.linkedin],
};

// FAQ Schema can have many questions and answers. These values are pulled from Contentful.
export const structuredFaq = (faqs: FaqSchema): FaqJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.FAQ_PAGE,
  mainEntity: faqs.map((faq) => ({
    '@type': SchemaType.QUESTION,
    name: faq.question,
    acceptedAnswer: {
      '@type': SchemaType.ANSWER,
      text: faq.answer,
    },
  })),
});
